/**
 * Sentry config shared between web-app (renderer) and electron-app (main process
 * + build). The Electron DSNs and the desktop release-name format must stay in
 * lockstep across both apps' runtime code and build configs — a rotation or
 * format change touching only one site would silently split events across
 * projects or break source-map resolution — so they live here.
 *
 * Keep this module dependency-free: it's imported from the browser renderer,
 * the Node main process, and vite build configs.
 */

// Electron Sentry projects (US region).
export const ELECTRON_DEV_DSN =
  'https://c5f3f2b6042f38d7c8c9a6480c28659a@o372418.ingest.us.sentry.io/4507902346330112'
export const ELECTRON_PROD_DSN =
  'https://42fd21c8ef4aad677d30fff3b38a3057@o372418.ingest.us.sentry.io/4507902350786560'

// Frozen at runtime but typed as T: Sentry's option types take mutable arrays.
const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(deepFreeze)
    Object.freeze(value)
  }
  return value
}

const SENSITIVE_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user']

/**
 * Sentry `dataCollection` for every app. SDK v11 collects IP addresses,
 * cookies, HTTP headers and bodies unless told otherwise; this is the v10
 * (`sendDefaultPii: false`) baseline from the v11 migration guide. Leaving the
 * option unset is not equivalent — it opts into the broader collection.
 *
 * Structurally typed so this module stays free of a `@sentry/*` dependency.
 */
export const SENTRY_DATA_COLLECTION = deepFreeze({
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: SENSITIVE_KEY_DENYLIST },
    response: { deny: SENSITIVE_KEY_DENYLIST },
  },
  httpBodies: [],
  urlQueryParams: { deny: SENSITIVE_KEY_DENYLIST },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
})

/**
 * Desktop release name — `desktop-app@{version}`, or `desktop-app@{version}+{channel}`
 * when a build channel is given. Used by main + renderer at runtime and by both
 * vite configs at build time; they must all produce the same string.
 */
export const desktopReleaseName = (version: string, channel?: string): string =>
  `desktop-app@${version}${channel ? `+${channel}` : ''}`

/**
 * Sentry environment for a desktop release channel. Sessions carry only
 * release + environment (never tags), so this is what makes crash-free rate
 * sliceable per channel — the `channel` tag cannot do it.
 *
 * `prod` maps to `production` rather than to itself so the environment filters
 * on the pre-existing desktop alerts and saved searches keep matching.
 */
export const desktopSentryEnvironment = (channel?: string | null): string => {
  if (!channel) return 'production'
  if (channel === 'prod') return 'production'
  if (channel === 'dev') return 'development'
  return channel
}
